import React, { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";
import {
  resetPasswordSchema,
  ResetPasswordFormValues,
} from "@shared/validations/resetPassword.schema";

// Types & Schemas
const ForgotPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onTouched",
  });

  const { id } = useParams();



  const payload = id;

  const onSubmit = useCallback(
    async (data: ResetPasswordFormValues) => {
      setIsLoading(true);
      try {
        const payload = {
          token: id,
          newPassword: data.newPassword,
        };
        await httpService.post(API_CONFIG.path.forgotPassword, payload);

        toast.success("Password reset successfully");
        navigate("/login");
      } catch (error: any) {
        toast.error(error?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  return (
    <div className="flex w-full items-center justify-center p-4 sm:p-6 md:w-1/2 md:p-8">
      <div className="w-full max-w-xs rounded-2xl border border-gray-200 bg-white px-4 py-5 shadow-sm sm:max-w-sm sm:rounded-3xl sm:p-6">
        {/* Logo for Mobile */}
        <div className="mb-4 block md:hidden">
          <img src="/goodweek_auth_mobile.svg" alt="Goodweeks Logo" width={150} height={50} className="text-white" />
        </div>

        {/* Heading */}
        <h1 className="mb-4 text-base font-bold text-gray-900 sm:mb-6 sm:text-xl md:text-2xl">
          Reset Password
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col pl-[2px] pr-4" noValidate>
          {/* Scrollable form fields */}
          <div className="max-h-[55vh] flex-grow overflow-y-auto pl-2 pr-4 sm:max-h-[60vh] md:max-h-full">
            {/* Password Field */}
            <div className="mb-4 sm:mb-3">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-10 sm:text-sm ${errors.newPassword ? "border-red-300" : "border-gray-300"
                    } ${isLoading ? "bg-gray-50" : ""}`}
                  placeholder="Enter New password"
                  disabled={isLoading}
                  {...register("newPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 transform p-1 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="mb-4 sm:mb-3">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-10 sm:text-sm ${errors.confirmPassword ? "border-red-300" : "border-gray-300"
                    } ${isLoading ? "bg-gray-50" : ""}`}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  {...register("confirmPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 transform p-1 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <Eye size={18} aria-hidden="true" />
                  ) : (
                    <EyeOff size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {/* Fixed bottom section */}
          <div className="border-t border-gray-100 pt-4">
            {/* Create Account Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-900 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-950 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-900 sm:py-3 sm:text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin sm:size-5" />
                  <span>Password Resetting...</span>
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
